import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './context/AuthContext';
import { GymConfigProvider } from './context/GymConfigContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Public Pages (carga inmediata)
import Login from './pages/Login';
import Unauthorized from './pages/Unauthorized';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Styles
import './styles/global.css';
import './styles/variables.css';
import './styles/components.css';
import './styles/pages.css';

// Admin Pages (lazy loading)
const DashboardAdmin = lazy(() => import('./pages/admin/DashboardAdmin'));
const Usuarios = lazy(() => import('./pages/admin/Usuarios'));
const CalendarioPage = lazy(() => import('./pages/admin/CalendarioPage'));
const GenerarHorariosAdmin = lazy(() => import('./pages/admin/GenerarHorariosAdmin'));
const AsignacionAutomaticaAdmin = lazy(() => import('./pages/admin/AsignacionAutomaticaAdmin'));
const Ejercicios = lazy(() => import('./pages/admin/Ejercicios'));
const Planes = lazy(() => import('./pages/admin/Planes'));
const Suscripciones = lazy(() => import('./pages/admin/Suscripciones'));
const Pagos = lazy(() => import('./pages/admin/Pagos'));
const Planificaciones = lazy(() => import('./pages/admin/Planificaciones'));
const Rutinas = lazy(() => import('./pages/admin/Rutinas'));
const Configuracion = lazy(() => import('./pages/admin/Configuracion'));
const VistaClase = lazy(() => import('./pages/admin/VistaClase'));

// Entrenador Pages (lazy loading)
const DashboardEntrenador = lazy(() => import('./pages/entrenador/DashboardEntrenador'));
const ClasesEntrenador = lazy(() => import('./pages/entrenador/ClasesEntrenador'));
const ClientesEntrenador = lazy(() => import('./pages/entrenador/ClientesEntrenador'));
const PlanificacionesEntrenador = lazy(() => import('./pages/entrenador/PlanificacionesEntrenador'));
const RutinasEntrenador = lazy(() => import('./pages/entrenador/RutinasEntrenador'));

// Cliente Pages (lazy loading)
const DashboardCliente = lazy(() => import('./pages/cliente/DashboardCliente'));
const HorariosCliente = lazy(() => import('./pages/cliente/HorariosCliente'));
const SesionesCliente = lazy(() => import('./pages/cliente/SesionesCliente'));
const PlanificacionesCliente = lazy(() => import('./pages/cliente/PlanificacionesCliente'));
const MiSuscripcion = lazy(() => import('./pages/cliente/MiSuscripcion'));
const MisRutinas = lazy(() => import('./pages/cliente/MisRutinas'));
const MiPerfil = lazy(() => import('./pages/cliente/MiPerfil'));
const MisPagos = lazy(() => import('./pages/cliente/MisPagos'));

function App() {
  return (
    <ErrorBoundary>
      <GymConfigProvider>
        <AuthProvider>
          <Router>
            <Toaster
              position="top-right"
              reverseOrder={false}
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                  fontSize: '14px',
                  borderRadius: '8px',
                  padding: '12px 16px',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#0096FF',
                    secondary: '#FFFFFF',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#FF4444',
                    secondary: '#FFFFFF',
                  },
                },
              }}
            />
            <Suspense fallback={<div className="loading">Cargando...</div>}>
        <Routes>
          {/* Rutas públicas */}
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Rutas de Admin */}
          <Route 
            path="/admin/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DashboardAdmin />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/usuarios" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Usuarios />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/generar-horarios" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <GenerarHorariosAdmin />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/asignacion-automatica"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AsignacionAutomaticaAdmin />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/admin/ejercicios" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Ejercicios />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/planes" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Planes />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/suscripciones" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Suscripciones />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/pagos" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Pagos />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/planificaciones" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Planificaciones />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/admin/calendario"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <CalendarioPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/rutinas"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Rutinas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/configuracion"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Configuracion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vista-clase"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <VistaClase />
              </ProtectedRoute>
            }
          />
          
          {/* Rutas de Entrenador */}
          <Route 
            path="/entrenador/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['entrenador']}>
                <DashboardEntrenador />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/entrenador/clases" 
            element={
              <ProtectedRoute allowedRoles={['entrenador']}>
                <ClasesEntrenador />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/entrenador/clientes" 
            element={
              <ProtectedRoute allowedRoles={['entrenador']}>
                <ClientesEntrenador />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/entrenador/planificaciones"
            element={
              <ProtectedRoute allowedRoles={['entrenador']}>
                <PlanificacionesEntrenador />
              </ProtectedRoute>
            }
          />
          <Route
            path="/entrenador/rutinas"
            element={
              <ProtectedRoute allowedRoles={['entrenador']}>
                <RutinasEntrenador />
              </ProtectedRoute>
            }
          />
          
          {/* Rutas de Cliente */}
          <Route 
            path="/cliente/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <DashboardCliente />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/cliente/horarios" 
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <HorariosCliente />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/cliente/sesiones" 
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <SesionesCliente />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/cliente/planificaciones" 
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <PlanificacionesCliente />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/cliente/suscripcion"
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <MiSuscripcion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cliente/rutinas"
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <MisRutinas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cliente/perfil"
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <MiPerfil />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cliente/pagos"
            element={
              <ProtectedRoute allowedRoles={['cliente']}>
                <MisPagos />
              </ProtectedRoute>
            }
          />
          
          {/* Redireccion por defecto */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
    </GymConfigProvider>
    </ErrorBoundary>
  );
}

export default App;
